// frontend/src/components/Auth.tsx
import React, { useState } from 'react';
import { AxiosError } from 'axios';
import apiClient from '../utils/apiClient';
import '../stylesNew.css';

interface AuthProps {
    onLogin: () => Promise<void>;
}

const Auth: React.FC<AuthProps> = ({ onLogin }) => {
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [username, setUsername] = useState<string>('');
    const [isRegistering, setIsRegistering] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [csrfToken, setCsrfToken] = useState<string>('');

    React.useEffect(() => {
        const fetchCsrfToken = async () => {
            try {
                const response = await apiClient.get('/api/csrf-token', { withCredentials: true });
                if (typeof response.data.csrfToken !== 'string') throw new Error('CSRF Token invalide');
                setCsrfToken(response.data.csrfToken);
            } catch (err) {
                console.error('Erreur lors de la récupération du CSRF token:', err);
                setError('Erreur de sécurité, veuillez réessayer');
            }
        };
        fetchCsrfToken();
    }, []);

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        setError(null);

        if (typeof email !== 'string' || !email.trim() || !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
            setError('Un email valide est requis');
            return;
        }
        if (typeof password !== 'string' || !password.trim() || password.length < 8 || !/[A-Za-z0-9!@#$%^&*]/.test(password)) {
            setError('Le mot de passe doit comporter au moins 8 caractères avec lettres et chiffres');
            return;
        }
        if (isRegistering && (typeof username !== 'string' || !username.trim() || username.length < 3)) {
            setError('Un nom d’utilisateur de 3 caractères minimum est requis');
            return;
        }
        if (!csrfToken) {
            setError('Erreur de sécurité (CSRF), veuillez réessayer');
            return;
        }

        const endpoint = isRegistering ? '/api/register' : '/api/login'; // Correction de l'URL
        const payload = isRegistering ? { username, email, password, csrfToken } : { email, password, csrfToken };
        console.log('Envoi au backend:', { ...payload, password: '****', csrfToken: '****' });

        try {
            const response = await apiClient.post(endpoint, payload, {
                withCredentials: true,
                headers: {
                    'X-CSRF-Token': csrfToken,
                },
            });

            if (!response.data || typeof response.data !== 'object') {
                throw new Error('Réponse du backend invalide');
            }
            console.log('Réponse du backend:', response.data);

            // Stockage sécurisé du token
            const token = response.data.token;
            if (token && typeof token === 'string' && token.trim()) {
                localStorage.setItem('token', token);
            } else {
                throw new Error('Token non trouvé dans la réponse');
            }

            setEmail('');
            setPassword('');
            setUsername('');
            await onLogin();
        } catch (err) {
            const error = err as AxiosError;
            const errorMessage =
                (error.response?.data as { error?: string })?.error ||
                error.message ||
                'Une erreur est survenue lors de l’authentification';
            console.error('Erreur lors de l’authentification:', errorMessage, error.response?.data);
            setError(errorMessage);
        }
    };

    const toggleRegister = (): void => {
        setIsRegistering((prev) => !prev);
        setError(null);
        setEmail('');
        setPassword('');
        setUsername('');
    };

    return (
        <div className="auth">
            <h2 className="auth__title">{isRegistering ? 'Inscription' : 'Connexion'}</h2>
            <form className="auth__form" onSubmit={handleSubmit}>
                {isRegistering && (
                    <div className="auth__field">
                        <label className="auth__label">Nom d’utilisateur :</label>
                        <input
                            className="auth__input"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            autoComplete="username"
                            maxLength={50}
                        />
                    </div>
                )}
                <div className="auth__field">
                    <label className="auth__label">Email :</label>
                    <input
                        className="auth__input"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                        maxLength={255}
                    />
                </div>
                <div className="auth__field">
                    <label className="auth__label">Mot de passe :</label>
                    <input
                        className="auth__input"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete={isRegistering ? 'new-password' : 'current-password'}
                        maxLength={128}
                    />
                </div>
                {error && <p className="auth__error">{error}</p>}
                <button className="auth__button" type="submit" disabled={!csrfToken}>
                    {isRegistering ? 'S’inscrire' : 'Se connecter'}
                </button>
            </form>
            <div className="auth__switch">
                <button className="auth__switch-button" onClick={toggleRegister}>
                    {isRegistering ? 'Passer à la connexion' : 'Passer à l’inscription'}
                </button>
            </div>
        </div>
    );
};

export default Auth;